import {
    Button,
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarRail,
    SidebarTrigger,
    useIsMobile,
    useSidebar,
} from '@quadminds/mindkit';
import { IAuthMeV2OutputDto, TypologiesEnum } from '@quadminds/shared/auth-system';
import { RiLoader4Line } from '@remixicon/react';
import { useQueryClient } from '@tanstack/react-query';
import { FileText, HelpCircle, MessageSquareX, Video } from 'lucide-react';
import * as React from 'react';
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ChatList from './ChatList/ChatList';
import NavButtons from './NavButtons/NavButtons';
import SearchForm from './SearchForm/SearchForm';
import useGetConversations from '../../../hooks/useGetConversations';
import { useIsKnowledge } from '../../../hooks/useIsKnowledge';
import { ConversationItem } from '../../../types/Conversation/Conversation';
import { requiresApiKey, shouldHideSuggestedQuestions } from '../../../utils/utils';
import { KnowledgeVideoModal } from '../../Onboarding/KnowledgeVideoModal';

const PAGE_SIZE = 50;

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
    const isMobile = useIsMobile();
    const { state } = useSidebar();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const scrollRef = useRef<HTMLDivElement | null>(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [queryChat, setQueryChat] = useState('');
    const [allConversations, setAllConversations] = useState<ConversationItem[]>([]);
    const [isKnowledgeVideoOpen, setIsKnowledgeVideoOpen] = useState(false);

    const { data: conversations = [], isLoading } = useGetConversations(currentPage, PAGE_SIZE);

    const queryClient = useQueryClient();
    const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']);

    const typologiesWithPersonalApiKey: TypologiesEnum[] = [TypologiesEnum.USER, TypologiesEnum.SUBUSER];
    const typologiesWithoutApiKeyRequirement: TypologiesEnum[] = [TypologiesEnum.MINDY_CLARO_KNOWLEDGE_USER];
    const { isKnowledge } = useIsKnowledge();

    const hasApiKeyProblem = (() => {
        if (!userInfo) return false;
        if (!requiresApiKey()) return false;
        if (typologiesWithoutApiKeyRequirement.includes(userInfo.typology)) return false;
        if (typologiesWithPersonalApiKey.includes(userInfo.typology)) return !userInfo.personalApiKey;
        return !userInfo.apiKey;
    })();

    useEffect(() => {
        if (conversations.length > 0) {
            setAllConversations((prev) => {
                if (currentPage === 1) return conversations;

                return [...prev, ...conversations];
            });
        } else if (currentPage === 1 && !isLoading) {
            setAllConversations([]);
        }
    }, [conversations, currentPage, isLoading]);

    const normalize = useCallback(
        (text: string) =>
            text
                .toLowerCase()
                .normalize('NFD')
                .replaceAll(/[\u0300-\u036f]/g, ''),
        [],
    );

    const hasMore = conversations.length === PAGE_SIZE;

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el || isLoading || !hasMore) return;

        const { scrollTop, clientHeight, scrollHeight } = el;

        if (scrollTop + clientHeight >= scrollHeight - 120) {
            setCurrentPage((prev) => prev + 1);
        }
    }, [isLoading, hasMore]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        el.addEventListener('scroll', handleScroll);
        return () => el.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    const filteredConversations = useMemo(() => {
        return [...allConversations].filter((c) => normalize(c.title).includes(normalize(queryChat)));
    }, [allConversations, queryChat, normalize]);

    let renderChatList;

    if (!filteredConversations.length && !isLoading && queryChat === '' && conversations.length === 0) {
        renderChatList = (
            <div className="flex items-center h-full p-4" data-testid="no-chats-loaded">
                <div className="flex flex-col items-center space-y-2 w-full">
                    <MessageSquareX className="text-secondary" />
                    <span className="text-center text-muted-foreground text-sm">{t('sideBar.chatList.noChats')}</span>
                </div>
            </div>
        );
    } else {
        renderChatList = (
            <>
                <SearchForm
                    query={queryChat}
                    handleQuery={(evt: React.ChangeEvent<HTMLInputElement>) => setQueryChat(evt.target.value)}
                />

                <ChatList items={filteredConversations} />

                {isLoading && (
                    <div className="flex justify-center py-3">
                        <RiLoader4Line size={25} className="animate-spin text-violet-600" />
                    </div>
                )}
            </>
        );
    }

    return (
        <Sidebar collapsible="icon" {...props}>
            <SidebarHeader>
                {!isMobile && (
                    <div className="ml-0.5">
                        <SidebarTrigger />
                    </div>
                )}
                <div className="-mx-2">
                    <NavButtons />
                </div>
            </SidebarHeader>

            <SidebarContent
                ref={scrollRef}
                className="overflow-x-hidden overflow-y-scroll scrollbar-thin scrollbar-thumb-gray-qm-100 dark:scrollbar-thumb-gray-qm-900 scrollbar-track-transparent"
            >
                {state === 'expanded' && !hasApiKeyProblem && renderChatList}
            </SidebarContent>
            <SidebarFooter>
                {state === 'expanded' && (
                    <>
                        <div className="border-t border-gray-200 dark:border-gray-700" />
                        <div className="flex flex-col">
                            {/* <Button
                                size="sm"
                                onClick={() => navigate('/release-notes')}
                                className="flex space-x-2 bg-gray-qm-900"
                                
                            >
                                <Bell className="text-foreground/70" size={16} />
                                <span className="text-sm">Novedades</span>
                            </Button> */}
                            <Button variant="ghost" onClick={() => navigate('/questions')} className="flex space-x-2">
                                <HelpCircle className="text-foreground/70" size={16} />
                                <span className="text-sm">Guía de Preguntas</span>
                            </Button>
                        </div>
                    </>
                )}
            </SidebarFooter>

            <SidebarFooter>
                {state === 'expanded' && (
                    <>
                        <div className="border-t border-gray-200 dark:border-gray-700" />
                        <div className="flex flex-col">
                            {isKnowledge ? (
                                <>
                                    <a
                                        className="flex items-center gap-2 px-2 py-2 text-base hover:bg-accent hover:text-accent-foreground rounded-sm cursor-pointer w-full"
                                        href="https://www.quadminds.com/descargas/manual-de-usuario-mindy-conocimiento/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <FileText className="text-foreground/70" />
                                        <span className="text-sm">{t('sideBar.userManual')}</span>
                                    </a>
                                    <button
                                        className="flex items-center gap-2 px-2 py-2 text-base hover:bg-accent hover:text-accent-foreground rounded-sm cursor-pointer w-full"
                                        onClick={() => setIsKnowledgeVideoOpen(true)}
                                    >
                                        <Video className="text-foreground/70" />
                                        <span className="text-sm">{t('sideBar.videoGuide')}</span>
                                    </button>
                                </>
                            ) : (
                                !shouldHideSuggestedQuestions(userInfo) && (
                                    <button
                                        className="flex items-center gap-2 px-2 py-2 text-base hover:bg-accent hover:text-accent-foreground rounded-sm cursor-pointer w-full"
                                        onClick={() => navigate('/suggested-questions')}
                                    >
                                        <HelpCircle className="text-foreground/70" />
                                        <span className="text-sm">Guía de Preguntas</span>
                                    </button>
                                )
                            )}
                        </div>
                    </>
                )}
            </SidebarFooter>
            <SidebarRail />
            <KnowledgeVideoModal isOpen={isKnowledgeVideoOpen} onOpenChange={setIsKnowledgeVideoOpen} />
        </Sidebar>
    );
}

export default AppSidebar;
